/**
 * DIMENSION serializer (§3.2 decision 4). The yap data-bag subtypes map
 * onto the DXF dimension types: linear→0 (rotated), aligned/continue/
 * baseline/arcLength→1 (aligned), diameter→3, radius/jogged→4, angular→5
 * (3-point), ordinate→6 (+64 for the X axis). Block-reference bit (32) and
 * the user-text bit (128) are always set, exactly like OCS. The anonymous
 * `*D` block is supplied by dimBlockBake (app-side baking). The tolerance
 * subtype exports as a native TOLERANCE (AcDbFcf) feature control frame —
 * full GD&T depth lands in Faz 8.
 */

import type { Entity } from '../../../../state/store';
import type { DxfWriteContext } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';
import { entityPrologue } from './common';
import { linearDimLinePoints, radialPoints } from '../dimBlockBake';
import { writeToleranceEntity } from './tolerance';

/** DXF dimension subclass record per mapped type. */
const SUBCLASS: Record<number, string> = {
    0: 'AcDbRotatedDimension',
    1: 'AcDbAlignedDimension',
    3: 'AcDbDiametricDimension',
    4: 'AcDbRadialDimension',
    5: 'AcDb3PointAngularDimension',
    6: 'AcDbOrdinateDimension',
};

export function writeDimension(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const d = e.data as Record<string, any>;
    if (d.type === 'tolerance') {
        writeToleranceEntity(w, ctx, e, owner);
        return;
    }
    entityPrologue(w, ctx, e, 'DIMENSION', owner);
    w.pair(100, 'AcDbDimension');
    const baked = ctx.dimBlocks.get(e.id);
    if (baked) w.pair(2, baked.name); // empty group 2 is an invalid table ref — omit
    w.pair(3, dimStyleName(ctx, d.styleId));
    if (typeof d.override === 'string' && d.override) w.pair(1, d.override);
    const flags = 32 | 128; // block-reference only + user-placed text
    switch (d.type) {
        case 'radius':
        case 'jogged': {
            const pts = radialPoints(d, false);
            if (!pts) return;
            w.pair(10, pts.b[0]).pair(20, pts.b[1]).pair(30, pts.b[2] ?? 0); // definition point on the circle
            w.pair(15, pts.a[0]).pair(25, pts.a[1]).pair(35, pts.a[2] ?? 0); // center
            w.pair(70, 4 | flags);
            w.pair(42, pts.radius);
            if (Array.isArray(d.textPosition)) {
                const tp = d.textPosition as number[];
                w.pair(44, Math.hypot(tp[0] - pts.b[0], tp[1] - pts.b[1]));
            }
            w.pair(100, SUBCLASS[4]);
            break;
        }
        case 'diameter': {
            const pts = radialPoints(d, true);
            if (!pts) return;
            w.pair(10, pts.b[0]).pair(20, pts.b[1]).pair(30, pts.b[2] ?? 0);
            w.pair(15, pts.a[0]).pair(25, pts.a[1]).pair(35, pts.a[2] ?? 0);
            w.pair(70, 3 | flags);
            w.pair(42, pts.radius * 2);
            if (Array.isArray(d.textPosition)) {
                const tp = d.textPosition as number[];
                w.pair(44, Math.hypot(tp[0] - pts.b[0], tp[1] - pts.b[1]));
            }
            w.pair(100, SUBCLASS[3]);
            break;
        }
        case 'angular': {
            const c = d.center as number[], p1 = d.point1 as number[], p2 = d.point2 as number[];
            if (!Array.isArray(c) || !Array.isArray(p1) || !Array.isArray(p2)) return;
            w.pair(13, p1[0] ?? 0).pair(23, p1[1] ?? 0).pair(33, p1[2] ?? 0);
            w.pair(14, p2[0] ?? 0).pair(24, p2[1] ?? 0).pair(34, p2[2] ?? 0);
            const tp = (d.textPosition as number[]) ?? c;
            w.pair(10, tp[0] ?? 0).pair(20, tp[1] ?? 0).pair(30, tp[2] ?? 0);
            w.pair(70, 5 | flags);
            // Angular measurement travels in radians.
            w.pair(42, typeof d.angle === 'number' ? (d.angle * Math.PI) / 180 : 0);
            w.pair(100, SUBCLASS[5]);
            break;
        }
        case 'ordinate': {
            const f = d.point1 as number[], l = (d.point2 ?? d.textPosition) as number[];
            if (!Array.isArray(f) || !Array.isArray(l)) return;
            w.pair(10, f[0] ?? 0).pair(20, f[1] ?? 0).pair(30, f[2] ?? 0);
            w.pair(13, l[0] ?? 0).pair(23, l[1] ?? 0).pair(33, l[2] ?? 0);
            w.pair(70, 6 | flags | (d.axis === 'x' ? 64 : 0));
            w.pair(42, d.axis === 'x' ? (f[0] ?? 0) : (f[1] ?? 0));
            w.pair(100, SUBCLASS[6]);
            break;
        }
        case 'linear': {
            const pts = linearDimLinePoints(d, false);
            if (!pts) return;
            w.pair(13, d.point1[0] ?? 0).pair(23, d.point1[1] ?? 0).pair(33, d.point1[2] ?? 0);
            w.pair(14, d.point2[0] ?? 0).pair(24, d.point2[1] ?? 0).pair(34, d.point2[2] ?? 0);
            const mid = [(pts.dimP1[0] + pts.dimP2[0]) / 2, (pts.dimP1[1] + pts.dimP2[1]) / 2, pts.dimP1[2] ?? 0];
            w.pair(10, mid[0]).pair(20, mid[1]).pair(30, mid[2]);
            writeTextMidpoint(w, d, mid);
            w.pair(70, 0 | flags);
            // Rotated linear: horizontal dim line → 0°, vertical → 90°.
            const horizontal = Math.abs((d.textPosition?.[1] ?? 0) - (d.point1?.[1] ?? 0)) > Math.abs((d.textPosition?.[0] ?? 0) - (d.point1?.[0] ?? 0));
            w.pair(53, horizontal ? 0 : 90);
            w.pair(42, pts.distance);
            w.pair(100, SUBCLASS[0]);
            break;
        }
        default: {
            // aligned / continue / baseline / arcLength → aligned dimension.
            const p1 = d.point1 as number[], p2 = d.point2 as number[];
            if (!Array.isArray(p1) || !Array.isArray(p2)) return;
            const pts = linearDimLinePoints(d, true);
            w.pair(13, p1[0] ?? 0).pair(23, p1[1] ?? 0).pair(33, p1[2] ?? 0);
            w.pair(14, p2[0] ?? 0).pair(24, p2[1] ?? 0).pair(34, p2[2] ?? 0);
            const mid = pts ? [(pts.dimP1[0] + pts.dimP2[0]) / 2, (pts.dimP1[1] + pts.dimP2[1]) / 2, pts.dimP1[2] ?? 0] : p1;
            w.pair(10, mid[0]).pair(20, mid[1]).pair(30, mid[2]);
            writeTextMidpoint(w, d, mid);
            w.pair(70, 1 | flags);
            const measured = Math.hypot((p2[0] ?? 0) - (p1[0] ?? 0), (p2[1] ?? 0) - (p1[1] ?? 0));
            w.pair(42, typeof d.arcLength === 'number' ? d.arcLength : measured);
            w.pair(100, SUBCLASS[1]);
            break;
        }
    }
}

/** Text midpoint (11) — the renderer lifts the label by the style gap. */
function writeTextMidpoint(w: DxfStreamWriter, d: Record<string, any>, mid: number[]): void {
    const tp = Array.isArray(d.textPosition) ? (d.textPosition as number[]) : mid;
    w.pair(11, tp[0] ?? 0).pair(21, tp[1] ?? 0).pair(31, tp[2] ?? 0);
}

function dimStyleName(ctx: DxfWriteContext, styleId: unknown): string {
    const entry = ctx.dimStyleEntries.get(typeof styleId === 'string' ? styleId : '');
    return entry?.name ?? 'Standard';
}
